/**
 * Prospecting Agent CRM - Page
 * P0 Priority Module for AI Marketing Engine
 * 
 * Features:
 * - Search for companies
 * - View and manage prospects
 * - Enrich prospects with contact data
 * - Qualify/reject leads
 * - Sync to Brevo CRM
 * - View action logs
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Users, 
  Building2, 
  Mail, 
  Phone, 
  Globe, 
  Linkedin,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Filter,
  Download,
  Upload,
  Settings,
  Activity,
  BarChart3,
  Zap,
  Clock,
  Star,
  ExternalLink,
  MoreVertical,
  Play,
  Pause,
  Eye,
  Trash2,
  Send,
  Sparkles
} from 'lucide-react';
import { apiClient } from '@/lib/api';
import { GlassCard, PillButton, StatusChip } from '../components/ios26/IOSComponents';
import AutonomousAgentPanel from './AutonomousAgentPanel';
import '../styles/ios26-design-system.css';

// Lead status colors and labels
const STATUS_CONFIG = {
  new: { label: 'Nouveau', color: 'hsl(210, 100%, 50%)', bg: 'hsl(210, 100%, 95%)' },
  qualified: { label: 'Qualifié', color: 'hsl(142, 70%, 45%)', bg: 'hsl(142, 70%, 95%)' },
  incomplete: { label: 'Incomplet', color: 'hsl(40, 100%, 50%)', bg: 'hsl(40, 100%, 95%)' },
  rejected: { label: 'Rejeté', color: 'hsl(0, 70%, 50%)', bg: 'hsl(0, 70%, 95%)' },
  synced_to_brevo: { label: 'Synchro Brevo', color: 'hsl(270, 70%, 50%)', bg: 'hsl(270, 70%, 95%)' },
  error: { label: 'Erreur', color: 'hsl(0, 100%, 40%)', bg: 'hsl(0, 100%, 95%)' }
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subtext, color }) => (
  <div 
    className="rounded-2xl p-5 transition-all hover:scale-[1.02]"
    style={{ 
      backgroundColor: 'hsl(var(--color-surface-2))',
      border: '1px solid hsl(var(--color-border-subtle))'
    }}
  >
    <div className="flex items-center gap-3 mb-3">
      <div 
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: color + '20', color }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-sm text-semantic-muted">{label}</span>
    </div>
    <div className="text-3xl font-bold text-semantic mb-1">{value}</div>
    {subtext && <div className="text-xs text-semantic-muted">{subtext}</div>}
  </div>
);

// Prospect Row Component
const ProspectRow = ({ prospect, onEnrich, onQualify, onReject, onSync, onTestSync, onView, enriching }) => {
  const [showActions, setShowActions] = useState(false);
  const status = STATUS_CONFIG[prospect.lead_status] || STATUS_CONFIG.new;
  
  return (
    <div 
      className="group p-4 rounded-xl transition-all hover:scale-[1.005]"
      style={{ 
        backgroundColor: 'hsl(var(--color-surface-2))',
        border: '1px solid hsl(var(--color-border-subtle))'
      }}
    >
      <div className="flex items-center gap-4">
        {/* Company Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-semantic-muted flex-shrink-0" />
            <span className="font-semibold text-semantic truncate">{prospect.company_name}</span>
            <span 
              className="px-2 py-0.5 rounded-full text-xs font-medium"
              style={{ backgroundColor: status.bg, color: status.color }}
            >
              {status.label}
            </span>
          </div>
          
          <div className="flex items-center gap-4 text-sm text-semantic-muted">
            {prospect.website && (
              <a 
                href={prospect.website.startsWith('http') ? prospect.website : `https://${prospect.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-semantic"
              >
                <Globe className="w-3.5 h-3.5" />
                {new URL(prospect.website.startsWith('http') ? prospect.website : `https://${prospect.website}`).hostname}
              </a>
            )}
            {prospect.industry && (
              <span className="flex items-center gap-1">
                <BarChart3 className="w-3.5 h-3.5" />
                {prospect.industry}
              </span>
            )}
            {prospect.country && (
              <span>{prospect.country}</span>
            )}
          </div>
        </div>
        
        {/* Contact Info */}
        <div className="flex-1 min-w-0">
          {prospect.contact_first_name || prospect.contact_last_name || prospect.contact_email ? (
            <div className="space-y-1">
              {(prospect.contact_first_name || prospect.contact_last_name) && (
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-semantic-muted" />
                  <span className="text-semantic">
                    {prospect.contact_first_name} {prospect.contact_last_name}
                  </span>
                  {prospect.contact_role && prospect.contact_role !== 'generic_business_contact' && (
                    <span className="text-xs text-semantic-muted">({prospect.contact_role})</span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3 text-sm flex-wrap">
                {prospect.contact_email && (
                  <a
                    href={`mailto:${prospect.contact_email}`}
                    className="flex items-center gap-1 text-semantic-muted hover:text-semantic"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    {prospect.contact_email}
                  </a>
                )}
                {prospect.is_generic_contact && (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ backgroundColor: 'hsl(40, 100%, 90%)', color: 'hsl(40, 100%, 30%)' }}
                    title="Email professionnel générique (contact@, hello@, info@, ...)"
                  >
                    générique
                  </span>
                )}
                {prospect.linkedin_url && (
                  <a
                    href={prospect.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-semantic-muted hover:text-semantic"
                  >
                    <Linkedin className="w-3.5 h-3.5" />
                    LinkedIn
                  </a>
                )}
              </div>
              {prospect.no_sync_reason && (
                <div className="text-xs text-semantic-muted italic" data-testid={`prospect-no-sync-reason-${prospect._id || prospect.id}`}>
                  Pas de sync : {(() => {
                    const reasons = {
                      no_contact_email: "aucun email trouvé",
                      no_valid_business_email: "aucun email professionnel valide",
                      score_below_threshold: "score sous le seuil",
                      already_synced: "déjà synchronisé",
                      simulation_mode: "mode simulation actif",
                      brevo_not_configured: "Brevo non configuré",
                      blacklisted: "contact blacklisté",
                      brevo_sync_failed: "échec sync Brevo",
                      rejected: "prospect rejeté",
                    };
                    return reasons[prospect.no_sync_reason] || prospect.no_sync_reason;
                  })()}
                </div>
              )}
              {prospect.contact_source_page && (
                <div className="text-xs text-semantic-muted truncate" title={prospect.contact_source_page}>
                  Source : <a href={prospect.contact_source_page} target="_blank" rel="noopener noreferrer" className="underline">{prospect.contact_source_page.replace(/^https?:\/\//, '').slice(0, 50)}</a>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-1">
              <div className="text-sm text-semantic-muted italic">
                Aucun contact extrait
              </div>
              {prospect.no_sync_reason && (
                <div className="text-xs text-semantic-muted">
                  Raison : {prospect.no_sync_reason === 'no_contact_email' ? "aucun email trouvé" : prospect.no_sync_reason}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Score */}
        <div className="w-20 text-center">
          <div 
            className="text-2xl font-bold"
            style={{ 
              color: prospect.opportunity_score >= 60 
                ? 'hsl(142, 70%, 45%)' 
                : prospect.opportunity_score >= 30 
                  ? 'hsl(40, 100%, 45%)' 
                  : 'hsl(0, 0%, 50%)'
            }}
          >
            {Math.round(prospect.opportunity_score)}
          </div>
          <div className="text-xs text-semantic-muted">Score</div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {!prospect.contact_email && prospect.lead_status !== 'synced_to_brevo' && (
            <PillButton
              size="sm"
              variant="ghost"
              onClick={() => onEnrich(prospect._id || prospect.id)}
              disabled={enriching}
              title="Enrichir les contacts"
              data-testid={`enrich-${prospect._id || prospect.id}`}
            >
              {enriching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            </PillButton>
          )}
          
          {prospect.lead_status === 'new' && (
            <>
              <PillButton
                size="sm"
                variant="ghost"
                onClick={() => onQualify(prospect._id || prospect.id)}
                title="Qualifier"
                data-testid={`qualify-${prospect._id || prospect.id}`}
              >
                <CheckCircle className="w-4 h-4 text-green-500" />
              </PillButton>
              <PillButton
                size="sm"
                variant="ghost"
                onClick={() => onReject(prospect._id || prospect.id)}
                title="Rejeter"
                data-testid={`reject-${prospect._id || prospect.id}`}
              >
                <XCircle className="w-4 h-4 text-red-500" />
              </PillButton>
            </>
          )}
          
          {prospect.lead_status === 'qualified' && prospect.contact_email && (
            <PillButton
              size="sm"
              variant="primary"
              onClick={() => onSync(prospect._id || prospect.id)}
              title="Synchroniser Brevo"
              data-testid={`sync-${prospect._id || prospect.id}`}
            >
              <Send className="w-4 h-4" />
            </PillButton>
          )}

          {/* P0: Test sync Brevo on this single contact (with confirmation) */}
          {prospect.contact_email && prospect.lead_status !== 'synced_to_brevo' && onTestSync && (
            <PillButton
              size="sm"
              variant="ghost"
              onClick={() => onTestSync(prospect)}
              title="Tester la sync Brevo sur ce contact (confirmation requise)"
              data-testid={`test-sync-${prospect._id || prospect.id}`}
            >
              <Send className="w-4 h-4 text-purple-500" />
            </PillButton>
          )}
          
          <PillButton
            size="sm"
            variant="ghost"
            onClick={() => onView(prospect)}
            title="Voir détails"
          >
            <Eye className="w-4 h-4" />
          </PillButton>
        </div>
      </div>
    </div>
  );
};

// Search Form Component
const SearchForm = ({ onSearch, loading }) => {
  const [query, setQuery] = useState('');
  const [industry, setIndustry] = useState('');
  const [country, setCountry] = useState('FR');
  const [numResults, setNumResults] = useState(20);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch({ query, industry, country, numResults });
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-semantic-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher des entreprises (ex: agences marketing Lyon, startups tech Paris...)"
            className="w-full pl-12 pr-4 py-3 rounded-xl text-semantic"
            style={{
              backgroundColor: 'hsl(var(--color-surface-2))',
              border: '1px solid hsl(var(--color-border))'
            }}
            required
            data-testid="search-query-input"
          />
        </div>
        
        <PillButton
          type="button"
          variant="ghost"
          onClick={() => setShowAdvanced(!showAdvanced)}
          data-testid="toggle-advanced-search"
        >
          <Filter className="w-4 h-4" />
          Filtres
          <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
        </PillButton>
        
        <PillButton
          type="submit"
          variant="primary"
          disabled={loading || !query.trim()}
          data-testid="search-submit-btn"
        >
          {loading ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
          Rechercher
        </PillButton>
      </div>
      
      {showAdvanced && (
        <div className="flex gap-4 p-4 rounded-xl" style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}>
          <div className="flex-1">
            <label className="block text-sm text-semantic-muted mb-1">Secteur d'activité</label>
            <input
              type="text"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="ex: Marketing, Tech, E-commerce..."
              className="w-full px-3 py-2 rounded-lg text-semantic"
              style={{
                backgroundColor: 'hsl(var(--color-surface))',
                border: '1px solid hsl(var(--color-border-subtle))'
              }}
              data-testid="search-industry-input"
            />
          </div>
          
          <div className="w-32">
            <label className="block text-sm text-semantic-muted mb-1">Pays</label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-semantic"
              style={{
                backgroundColor: 'hsl(var(--color-surface))',
                border: '1px solid hsl(var(--color-border-subtle))'
              }}
              data-testid="search-country-select"
            >
              <option value="FR">France</option>
              <option value="US">États-Unis</option>
              <option value="GB">Royaume-Uni</option>
              <option value="DE">Allemagne</option>
              <option value="ES">Espagne</option>
            </select>
          </div>
          
          <div className="w-32">
            <label className="block text-sm text-semantic-muted mb-1">Résultats</label>
            <select
              value={numResults}
              onChange={(e) => setNumResults(Number(e.target.value))}
              className="w-full px-3 py-2 rounded-lg text-semantic"
              style={{
                backgroundColor: 'hsl(var(--color-surface))',
                border: '1px solid hsl(var(--color-border-subtle))'
              }}
              data-testid="search-num-results"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      )}
    </form>
  );
};

// Main Page Component
export default function ProspectingAgentPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [enriching, setEnriching] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  const [status, setStatus] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [totalProspects, setTotalProspects] = useState(0);
  const [logs, setLogs] = useState([]);
  
  const [activeTab, setActiveTab] = useState('prospects');
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedProspect, setSelectedProspect] = useState(null);
  
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // P0: Dry-run mode toggle (default = true for safety)
  // Stored in localStorage so the user's preference persists between sessions
  const [dryRun, setDryRun] = useState(() => {
    const saved = localStorage.getItem('prospecting_dry_run');
    return saved === null ? true : saved === 'true';
  });
  // P0: Confirmation modal state for live (non-dry-run) sync
  const [confirmSyncPayload, setConfirmSyncPayload] = useState(null);
  // P0: Test single-contact Brevo sync state
  const [testSyncProspect, setTestSyncProspect] = useState(null);
  const [testSyncResult, setTestSyncResult] = useState(null);
  const [testSyncing, setTestSyncing] = useState(false);

  useEffect(() => {
    localStorage.setItem('prospecting_dry_run', String(dryRun));
  }, [dryRun]);
  
  // P0: Top-level mode - autonomous (default) or manual research
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('prospecting_mode');
    return saved === 'manual' ? 'manual' : 'autonomous';
  });

  useEffect(() => {
    localStorage.setItem('prospecting_mode', mode);
  }, [mode]);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [statusRes, prospectsRes] = await Promise.all([
        apiClient.get('/prospecting-agent/status'),
        apiClient.get('/prospecting-agent/prospects')
      ]);
      
      setStatus(statusRes.data);
      setProspects(prospectsRes.data.prospects || []);
      setTotalProspects(prospectsRes.data.total || 0);
    } catch (err) {
      console.error('Error loading prospecting data:', err);
      setError('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };
  
  const loadProspects = async (filter = statusFilter) => {
    try {
      const params = new URLSearchParams();
      if (filter) params.append('status', filter);
      
      const res = await apiClient.get(`/prospecting-agent/prospects?${params}`);
      setProspects(res.data.prospects || []);
      setTotalProspects(res.data.total || 0);
    } catch (err) {
      console.error('Error loading prospects:', err);
    }
  };
  
  const loadLogs = async () => {
    try {
      const res = await apiClient.get('/prospecting-agent/logs?limit=50');
      setLogs(res.data.logs || []);
    } catch (err) {
      console.error('Error loading logs:', err);
    }
  };
  
  // Search companies
  const handleSearch = async (searchParams) => {
    setSearching(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const res = await apiClient.post('/prospecting-agent/search', {
        query: searchParams.query,
        industry: searchParams.industry || null,
        country: searchParams.country,
        num_results: searchParams.numResults
      });
      
      if (res.data.success) {
        setSuccessMessage(`${res.data.new_prospects} nouveaux prospects ajoutés (${res.data.duplicates_skipped} doublons ignorés)`);
        loadProspects();
        loadData(); // Refresh stats
      } else {
        setError(res.data.error || 'Erreur lors de la recherche');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la recherche');
    } finally {
      setSearching(false);
    }
  };
  
  // Enrich prospect
  const handleEnrich = async (prospectId) => {
    setEnriching(prospectId);
    setError(null);
    
    try {
      const res = await apiClient.post('/prospecting-agent/enrich', {
        prospect_id: prospectId
      });
      
      if (res.data.success) {
        setSuccessMessage(`${res.data.contacts_found} contacts trouvés`);
        loadProspects();
      } else {
        setError(res.data.error || 'Aucun contact trouvé');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'enrichissement');
    } finally {
      setEnriching(null);
    }
  };
  
  // Qualify/Reject prospect
  const handleQualify = async (prospectId, qualified = true) => {
    try {
      await apiClient.post('/prospecting-agent/qualify', {
        prospect_id: prospectId,
        qualified
      });
      
      setSuccessMessage(qualified ? 'Prospect qualifié' : 'Prospect rejeté');
      loadProspects();
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    }
  };
  
  // Sync to Brevo
  // P0: Always honor dry-run flag from UI; show confirmation modal for live sync
  const handleSyncBrevo = async (prospectId = null, syncAll = false, forceLive = false) => {
    // If user is in dry-run mode and not forcing live, just run dry-run directly
    if (!forceLive && !dryRun) {
      // Live mode: ask for confirmation first
      setConfirmSyncPayload({ prospectId, syncAll });
      return;
    }

    setSyncing(true);
    setError(null);

    try {
      const res = await apiClient.post('/prospecting-agent/sync-brevo', {
        prospect_id: prospectId,
        sync_all_qualified: syncAll,
        dry_run: dryRun
      });

      if (res.data.success) {
        if (res.data.dry_run || dryRun) {
          setSuccessMessage(`[SIMULATION] ${res.data.synced} contact(s) seraient synchronisés avec Brevo. Désactivez le mode simulation pour la sync réelle.`);
        } else {
          setSuccessMessage(`${res.data.synced} contact(s) synchronisé(s) avec Brevo`);
        }
        loadProspects();
        loadData();
      } else {
        setError(res.data.errors?.map(e => e.error).join(', ') || 'Erreur de synchronisation');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de synchronisation Brevo');
    } finally {
      setSyncing(false);
      setConfirmSyncPayload(null);
    }
  };

  const handleConfirmLiveSync = () => {
    if (!confirmSyncPayload) return;
    const { prospectId, syncAll } = confirmSyncPayload;
    handleSyncBrevo(prospectId, syncAll, true);
  };

  // P0: Test sync a SINGLE prospect against Brevo (with explicit confirmation)
  const handleTestSyncConfirm = async () => {
    if (!testSyncProspect) return;
    setTestSyncing(true);
    setTestSyncResult(null);
    try {
      const id = testSyncProspect._id || testSyncProspect.id;
      const res = await apiClient.post(
        `/prospecting-agent/prospect/${id}/test-sync-brevo`,
        { confirm: true }
      );
      setTestSyncResult(res.data);
      // Refresh list to reflect any new brevo_sync_status
      await loadProspects();
      await loadData();
    } catch (err) {
      setTestSyncResult({
        success: false,
        code: 'request_failed',
        message: err?.response?.data?.detail || 'Erreur de requête',
      });
    } finally {
      setTestSyncing(false);
    }
  };
  
  // Tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'logs' && logs.length === 0) {
      loadLogs();
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen ios-bg-mesh flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-semantic-muted" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen ios-bg-mesh">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-semantic mb-2">
                Agent de Prospection CRM
              </h1>
              <p className="text-semantic-muted">
                Recherchez des entreprises, enrichissez les contacts et synchronisez avec Brevo
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* P0: Dry-run mode toggle - only relevant in manual mode */}
              {mode === 'manual' && (
              <div
                className="flex items-center gap-3 px-4 py-2 rounded-xl"
                style={{
                  backgroundColor: dryRun ? 'hsl(40, 100%, 95%)' : 'hsl(0, 70%, 95%)',
                  border: `2px solid ${dryRun ? 'hsl(40, 100%, 50%)' : 'hsl(0, 70%, 50%)'}`
                }}
                data-testid="dry-run-toggle-container"
              >
                <div className="flex flex-col">
                  <span
                    className="text-xs font-bold uppercase tracking-wide"
                    style={{ color: dryRun ? 'hsl(40, 100%, 35%)' : 'hsl(0, 70%, 40%)' }}
                  >
                    {dryRun ? 'Mode Simulation' : 'Mode Live'}
                  </span>
                  <span className="text-xs text-semantic-muted">
                    {dryRun ? 'Aucune action sur Brevo' : 'Sync RÉELLE sur Brevo'}
                  </span>
                </div>
                <button
                  onClick={() => setDryRun(!dryRun)}
                  className="relative w-12 h-6 rounded-full transition-colors"
                  style={{
                    backgroundColor: dryRun ? 'hsl(40, 100%, 50%)' : 'hsl(0, 70%, 50%)'
                  }}
                  data-testid="dry-run-toggle"
                  aria-label="Basculer le mode simulation"
                >
                  <span
                    className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform"
                    style={{
                      transform: dryRun ? 'translateX(2px)' : 'translateX(26px)'
                    }}
                  />
                </button>
              </div>
              )}

              {status?.configured?.serper_api === false && (
                <div 
                  className="px-4 py-2 rounded-xl text-sm flex items-center gap-2"
                  style={{ 
                    backgroundColor: 'hsl(40, 100%, 95%)',
                    color: 'hsl(40, 100%, 35%)'
                  }}
                >
                  <AlertCircle className="w-4 h-4" />
                  Serper API non configurée
                </div>
              )}
              
              <PillButton
                variant="ghost"
                onClick={loadData}
                title="Rafraîchir"
              >
                <RefreshCw className="w-4 h-4" />
              </PillButton>
            </div>
          </div>
          
          {/* Stats - P0 (iter 119): replaced "Restants aujourd'hui (Limite 50/jour)"
               with the technical raw cap so it cannot be confused with the
               business objective ("Objectif quotidien" lives in autonomous tab). */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={Users}
              label="Total Prospects"
              value={status?.stats?.total_prospects || 0}
              color="hsl(210, 100%, 50%)"
            />
            <StatCard
              icon={CheckCircle}
              label="Qualifiés"
              value={status?.stats?.by_status?.qualified || 0}
              color="hsl(142, 70%, 45%)"
            />
            <StatCard
              icon={Send}
              label="Synchro Brevo"
              value={status?.stats?.by_status?.synced_to_brevo || 0}
              color="hsl(270, 70%, 50%)"
            />
            <StatCard
              icon={Clock}
              label="Cap technique 24h"
              value={`${status?.stats?.total_prospects || 0} / ${status?.daily_limit || 300}`}
              subtext="Cap technique uniquement"
              color="hsl(40, 100%, 50%)"
            />
          </div>
        </div>

        {/* P0: Top-level mode tabs (Autonomous first, Manual second) */}
        <div className="flex items-center gap-2 mb-6" data-testid="prospecting-mode-tabs">
          <button
            onClick={() => setMode('autonomous')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all"
            style={{
              backgroundColor: mode === 'autonomous' ? 'hsl(142, 70%, 45%)' : 'hsl(var(--color-surface-2))',
              color: mode === 'autonomous' ? 'white' : 'hsl(var(--color-text-2))',
              boxShadow: mode === 'autonomous' ? '0 4px 12px hsl(142 70% 45% / 0.25)' : 'none',
            }}
            data-testid="mode-tab-autonomous"
          >
            <Sparkles className="w-4 h-4" />
            Agent autonome
          </button>
          <button
            onClick={() => setMode('manual')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
            style={{
              backgroundColor: mode === 'manual' ? 'hsl(var(--color-primary))' : 'hsl(var(--color-surface-2))',
              color: mode === 'manual' ? 'hsl(var(--color-primary-text))' : 'hsl(var(--color-text-2))',
            }}
            data-testid="mode-tab-manual"
          >
            <Search className="w-4 h-4" />
            Recherche manuelle
          </button>
        </div>

        {/* === Autonomous panel === */}
        {mode === 'autonomous' && <AutonomousAgentPanel />}
        
        {/* Messages */}
        {error && (
          <div 
            className="mb-6 p-4 rounded-xl flex items-center gap-3"
            style={{ backgroundColor: 'hsl(0, 100%, 95%)', color: 'hsl(0, 70%, 40%)' }}
          >
            <AlertCircle className="w-5 h-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">×</button>
          </div>
        )}
        
        {successMessage && (
          <div 
            className="mb-6 p-4 rounded-xl flex items-center gap-3"
            style={{ backgroundColor: 'hsl(142, 70%, 95%)', color: 'hsl(142, 70%, 30%)' }}
          >
            <CheckCircle className="w-5 h-5" />
            {successMessage}
            <button onClick={() => setSuccessMessage(null)} className="ml-auto">×</button>
          </div>
        )}
        
        {/* === Manual research mode (legacy UI) === */}
        {mode === 'manual' && (<>
        {/* Search */}
        <GlassCard className="mb-6 p-6">
          <h2 className="text-lg font-semibold text-semantic mb-4 flex items-center gap-2">
            <Search className="w-5 h-5" />
            Recherche d'entreprises
          </h2>
          <SearchForm onSearch={handleSearch} loading={searching} />
        </GlassCard>
        
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => handleTabChange('prospects')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeTab === 'prospects' 
                ? 'bg-primary text-white' 
                : 'text-semantic-muted hover:text-semantic'
            }`}
            style={activeTab === 'prospects' ? { backgroundColor: 'hsl(var(--color-primary))' } : {}}
            data-testid="tab-prospects"
          >
            Prospects ({totalProspects})
          </button>
          <button
            onClick={() => handleTabChange('logs')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeTab === 'logs' 
                ? 'bg-primary text-white' 
                : 'text-semantic-muted hover:text-semantic'
            }`}
            style={activeTab === 'logs' ? { backgroundColor: 'hsl(var(--color-primary))' } : {}}
            data-testid="tab-logs"
          >
            Historique
          </button>
          
          <div className="flex-1" />
          
          {activeTab === 'prospects' && (
            <>
              {/* Status filter */}
              <select
                value={statusFilter || ''}
                onChange={(e) => {
                  setStatusFilter(e.target.value || null);
                  loadProspects(e.target.value || null);
                }}
                className="px-3 py-2 rounded-lg text-sm text-semantic"
                style={{
                  backgroundColor: 'hsl(var(--color-surface-2))',
                  border: '1px solid hsl(var(--color-border-subtle))'
                }}
                data-testid="status-filter"
              >
                <option value="">Tous les statuts</option>
                {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              
              {/* Sync all button */}
              <PillButton
                variant="primary"
                onClick={() => handleSyncBrevo(null, true)}
                disabled={syncing || !status?.stats?.by_status?.qualified}
                data-testid="sync-all-brevo"
              >
                {syncing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Sync tous les qualifiés
              </PillButton>
            </>
          )}
        </div>
        
        {/* Content */}
        {activeTab === 'prospects' && (
          <div className="space-y-3">
            {prospects.length === 0 ? (
              <div 
                className="text-center py-12 rounded-2xl"
                style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
              >
                <Users className="w-12 h-12 mx-auto mb-4 text-semantic-muted" />
                <h3 className="text-lg font-semibold text-semantic mb-2">
                  Aucun prospect
                </h3>
                <p className="text-semantic-muted mb-4">
                  Lancez une recherche pour trouver des entreprises
                </p>
              </div>
            ) : (
              prospects.map((prospect) => (
                <ProspectRow
                  key={prospect._id || prospect.id}
                  prospect={prospect}
                  onEnrich={handleEnrich}
                  onQualify={(id) => handleQualify(id, true)}
                  onReject={(id) => handleQualify(id, false)}
                  onSync={handleSyncBrevo}
                  onTestSync={(p) => setTestSyncProspect(p)}
                  onView={setSelectedProspect}
                  enriching={enriching === (prospect._id || prospect.id)}
                />
              ))
            )}
          </div>
        )}
        
        {activeTab === 'logs' && (
          <div className="space-y-2">
            {logs.length === 0 ? (
              <div 
                className="text-center py-12 rounded-2xl"
                style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
              >
                <Activity className="w-12 h-12 mx-auto mb-4 text-semantic-muted" />
                <h3 className="text-lg font-semibold text-semantic">
                  Aucune activité
                </h3>
              </div>
            ) : (
              logs.map((log, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-xl flex items-center gap-4"
                  style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
                >
                  <div 
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      log.success ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                    }`}
                  >
                    {log.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-semantic">{log.action}</div>
                    {log.details && (
                      <div className="text-sm text-semantic-muted">
                        {JSON.stringify(log.details)}
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-semantic-muted">
                    {new Date(log.created_at).toLocaleString('fr-FR')}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        
        {/* P0: Live Sync Confirmation Modal */}
        {confirmSyncPayload && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
            onClick={() => setConfirmSyncPayload(null)}
            data-testid="live-sync-confirm-modal"
          >
            <div
              className="w-full max-w-lg rounded-2xl p-6"
              style={{ backgroundColor: 'hsl(var(--color-surface))' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: 'hsl(0, 70%, 95%)' }}
                >
                  <AlertCircle className="w-6 h-6" style={{ color: 'hsl(0, 70%, 50%)' }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-semantic">Confirmer la synchronisation Brevo</h3>
                  <p className="text-sm text-semantic-muted">Cette action enverra les contacts à votre compte Brevo</p>
                </div>
              </div>

              <div
                className="p-4 rounded-xl mb-4"
                style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
              >
                <p className="text-sm text-semantic mb-2">
                  <strong>Cible :</strong>{' '}
                  {confirmSyncPayload.syncAll
                    ? `Tous les prospects qualifiés (${status?.stats?.by_status?.qualified || 0})`
                    : '1 prospect'}
                </p>
                <p className="text-sm text-semantic">
                  <strong>Mode :</strong>{' '}
                  <span style={{ color: 'hsl(0, 70%, 40%)', fontWeight: 600 }}>LIVE — sync réelle</span>
                </p>
              </div>

              <p className="text-sm text-semantic-muted mb-4">
                Vous êtes sur le point de pousser ces contacts vers Brevo. Cette action n'est pas réversible côté Brevo.
              </p>

              <div className="flex gap-3">
                <PillButton
                  variant="outline"
                  className="flex-1"
                  onClick={() => setConfirmSyncPayload(null)}
                  data-testid="cancel-live-sync-btn"
                >
                  Annuler
                </PillButton>
                <PillButton
                  variant="primary"
                  className="flex-1"
                  onClick={handleConfirmLiveSync}
                  disabled={syncing}
                  data-testid="confirm-live-sync-btn"
                >
                  {syncing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Synchronisation...
                    </>
                  ) : (
                    'Confirmer la sync réelle'
                  )}
                </PillButton>
              </div>
            </div>
          </div>
        )}

        {/* P0: Single-prospect test-sync Brevo modal */}
        {testSyncProspect && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
            onClick={() => { setTestSyncProspect(null); setTestSyncResult(null); }}
            data-testid="test-sync-modal"
          >
            <div
              className="w-full max-w-lg rounded-2xl p-6"
              style={{ backgroundColor: 'hsl(var(--color-surface))' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: 'hsl(270, 70%, 95%)' }}
                >
                  <Send className="w-6 h-6" style={{ color: 'hsl(270, 70%, 50%)' }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-semantic">Tester la sync Brevo</h3>
                  <p className="text-sm text-semantic-muted">
                    {testSyncProspect.contact_email}
                  </p>
                </div>
              </div>

              <div
                className="p-4 rounded-xl mb-4 text-sm space-y-1"
                style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
              >
                <p><strong>Prospect :</strong> {testSyncProspect.company_name}</p>
                <p><strong>Email :</strong> {testSyncProspect.contact_email}</p>
                <p><strong>Score :</strong> {Math.round(testSyncProspect.opportunity_score || 0)}/100</p>
                <p className="text-xs text-semantic-muted">
                  Cette action ignore le mode simulation mais respecte tous les
                  contrôles : email valide, blacklist, doublon, configuration Brevo.
                </p>
              </div>

              {testSyncResult && (
                <div
                  className="p-3 rounded-xl mb-4 text-sm"
                  style={{
                    backgroundColor: testSyncResult.success ? 'hsl(142, 70%, 95%)' : 'hsl(0, 70%, 95%)',
                    color: testSyncResult.success ? 'hsl(142, 70%, 30%)' : 'hsl(0, 70%, 35%)',
                  }}
                  data-testid="test-sync-result"
                >
                  <div className="font-medium">
                    {testSyncResult.success ? '✓ Succès' : '✗ Échec'} — {testSyncResult.code}
                  </div>
                  <div className="text-xs">{testSyncResult.message}</div>
                </div>
              )}

              <div className="flex gap-3">
                <PillButton
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setTestSyncProspect(null); setTestSyncResult(null); }}
                  data-testid="cancel-test-sync-btn"
                >
                  Fermer
                </PillButton>
                {!testSyncResult?.success && (
                  <PillButton
                    variant="primary"
                    className="flex-1"
                    onClick={handleTestSyncConfirm}
                    disabled={testSyncing}
                    data-testid="confirm-test-sync-btn"
                  >
                    {testSyncing ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Synchronisation…
                      </>
                    ) : (
                      'Confirmer & synchroniser'
                    )}
                  </PillButton>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Prospect Detail Modal */}
        {selectedProspect && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={() => setSelectedProspect(null)}
          >
            <div 
              className="w-full max-w-2xl rounded-2xl p-6 max-h-[80vh] overflow-y-auto"
              style={{ backgroundColor: 'hsl(var(--color-surface))' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-semantic">
                  {selectedProspect.company_name}
                </h2>
                <button onClick={() => setSelectedProspect(null)}>
                  <XCircle className="w-6 h-6 text-semantic-muted" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-semantic-muted">Site web</label>
                    <a 
                      href={selectedProspect.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-semantic hover:underline"
                    >
                      {selectedProspect.website}
                    </a>
                  </div>
                  <div>
                    <label className="text-sm text-semantic-muted">Secteur</label>
                    <p className="text-semantic">{selectedProspect.industry || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-semantic-muted">Pays</label>
                    <p className="text-semantic">{selectedProspect.country || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-semantic-muted">Score</label>
                    <p className="text-semantic">{Math.round(selectedProspect.opportunity_score)}/100</p>
                  </div>
                </div>
                
                {(selectedProspect.contact_first_name || selectedProspect.contact_email) && (
                  <>
                    <hr style={{ borderColor: 'hsl(var(--color-border))' }} />
                    <h3 className="font-semibold text-semantic">Contact principal</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-semantic-muted">Nom</label>
                        <p className="text-semantic">
                          {selectedProspect.contact_first_name} {selectedProspect.contact_last_name}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-semantic-muted">Fonction</label>
                        <p className="text-semantic">{selectedProspect.contact_role || '-'}</p>
                      </div>
                      <div>
                        <label className="text-sm text-semantic-muted">Email</label>
                        <p className="text-semantic">{selectedProspect.contact_email || '-'}</p>
                      </div>
                      <div>
                        <label className="text-sm text-semantic-muted">LinkedIn</label>
                        {selectedProspect.linkedin_url ? (
                          <a 
                            href={selectedProspect.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-semantic hover:underline"
                          >
                            Voir profil
                          </a>
                        ) : (
                          <p className="text-semantic">-</p>
                        )}
                      </div>
                    </div>
                  </>
                )}
                
                <hr style={{ borderColor: 'hsl(var(--color-border))' }} />
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-semantic-muted">Source</label>
                    <p className="text-semantic">{selectedProspect.source_type}</p>
                  </div>
                  <div>
                    <label className="text-semantic-muted">Créé le</label>
                    <p className="text-semantic">
                      {new Date(selectedProspect.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        </>)}
      </div>
    </div>
  );
}
